import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    ClockCircleOutlined, 
    SyncOutlined, 
    CheckCircleOutlined, 
    BarChartOutlined 
} from '@ant-design/icons';
import './FleetStatusChart.css';

// 一行最多同时显示的卡片数（超过则启动传送带式轮播）
const MAX_VISIBLE_CARDS = 4;
// 轮播步进间隔与单步滑动动画时长（与 CSS .sliding 的 transition 时长保持一致）
const ROTATE_INTERVAL_MS = 10000;
const SLIDE_ANIM_MS = 800;

/**
 * 机队技术状态监控 - 单行卡片视图
 *
 * 显示策略：
 * - 飞机数 <= MAX_VISIBLE_CARDS：全部一行排开，不轮播
 * - 飞机数 >  MAX_VISIBLE_CARDS：传送带式轮播。多渲染 1 张作为"下一张"预备，
 *   每步把整条轨道向左平移一个卡位（最左滑出、中间平移、最右滑入），
 *   平移结束后瞬间把轨道复位并推进数据窗口。卡片以注册号为 key，
 *   React 复用同一批 DOM 节点，复位时画面无跳变、无闪烁。
 */
const FleetStatusChart = ({ data = [], loading = false }) => {
    const [windowStart, setWindowStart] = useState(0); // 滑动窗口起点（飞机索引）
    const [sliding, setSliding] = useState(false); // true=正在向左平移；false=已复位静止

    const total = data?.length || 0;
    const needCarousel = total > MAX_VISIBLE_CARDS;

    // 机队数量变化时回到窗口起点，避免起点越界
    useEffect(() => {
        setWindowStart(0);
        setSliding(false);
    }, [total]);

    // 传送带轮播：平移一格 → 平移结束后复位轨道并推进窗口（两步同批更新，无跳变）
    useEffect(() => {
        if (!needCarousel) return;

        let settleTimer;
        const timer = setInterval(() => {
            setSliding(true);
            settleTimer = setTimeout(() => {
                setWindowStart(prev => (prev + 1) % total);
                setSliding(false);
            }, SLIDE_ANIM_MS);
        }, ROTATE_INTERVAL_MS);

        return () => {
            clearInterval(timer);
            clearTimeout(settleTimer);
        };
    }, [needCarousel, total]);

    // 当前可见卡片：不轮播时全量；轮播时取循环窗口并多取 1 张作为滑入的"下一张"
    const getCurrentCards = () => {
        if (!needCarousel) return data || [];
        return Array.from(
            { length: MAX_VISIBLE_CARDS + 1 },
            (_, i) => data[(windowStart + i) % total]
        );
    };

    // 获取状态样式类名（按技术方案：>=90好, 75-89警告, <75差）
    const getStatusClass = (value) => {
        const val = parseFloat(value);
        if (isNaN(val)) return '';
        if (val >= 90) return 'status-good';
        if (val >= 75) return 'status-warning';
        return 'status-bad';
    };

    if (loading) return <div className="fleet-loading">加载中...</div>;
    if (!data || data.length === 0) return <div className="fleet-empty">暂无机队数据</div>;

    const currentCards = getCurrentCards();

    return (
        <div className="fleet-carousel-container">
            {/* 卡片容器 */}
            <div className={`fleet-cards-row${needCarousel ? ' is-carousel' : ''}${sliding ? ' sliding' : ' idle'}`}>
                {currentCards.map((item) => {
                    const monthlyBH = item.monthly_bh !== undefined && item.monthly_bh !== null ? item.monthly_bh : 0;
                    const monthlyCycle = item.monthly_cycle !== undefined && item.monthly_cycle !== null ? item.monthly_cycle : 0;
                    
                    // 准点率：处理 null/undefined/NaN 的情况
                    const rawOnTimeRate = item.monthly_on_time_rate;
                    const onTimeRate = (rawOnTimeRate !== undefined && rawOnTimeRate !== null && !isNaN(rawOnTimeRate)) 
                        ? parseFloat(rawOnTimeRate) 
                        : null;
                    
                    // 日均BH：处理 null/undefined/NaN 的情况
                    const rawBhDailyAvg = item.bh_daily_avg;
                    const bhDailyAvg = (rawBhDailyAvg !== undefined && rawBhDailyAvg !== null && !isNaN(rawBhDailyAvg)) 
                        ? parseFloat(rawBhDailyAvg) 
                        : null;
                    
                    return (
                        <div key={item.aircraft_registration} className="fleet-card">
                            <div className="card-header">
                                <div className="reg-no">{item.aircraft_registration}</div>
                            </div>

                            <div className="card-metrics-grid">
                                <div className="metric-box">
                                    <div className="metric-icon"><ClockCircleOutlined /></div>
                                    <div className="metric-content">
                                        <div className="metric-label-box">月BH</div>
                                        <div className="metric-value-box">{monthlyBH}h</div>
                                    </div>
                                </div>

                                <div className="metric-box">
                                    <div className="metric-icon"><SyncOutlined /></div>
                                    <div className="metric-content">
                                        <div className="metric-label-box">月Cycle</div>
                                        <div className="metric-value-box">{monthlyCycle}次</div>
                                    </div>
                                </div>

                                <div className="metric-box">
                                    <div className="metric-icon"><CheckCircleOutlined /></div>
                                    <div className="metric-content">
                                        <div className="metric-label-box">准点率</div>
                                        <div className={`metric-value-box ${onTimeRate !== null ? getStatusClass(onTimeRate) : ''}`}>
                                            {onTimeRate !== null ? `${onTimeRate}%` : '--'}
                                        </div>
                                    </div>
                                </div>

                                <div className="metric-box">
                                    <div className="metric-icon"><BarChartOutlined /></div>
                                    <div className="metric-content">
                                        <div className="metric-label-box">利用率</div>
                                        <div className="metric-value-box">
                                            {bhDailyAvg !== null ? `${bhDailyAvg}h` : '--'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
        </div>
    );
};

FleetStatusChart.propTypes = {
    data: PropTypes.array,
    loading: PropTypes.bool
};

export default memo(FleetStatusChart);
